import { legacyAddressTarget } from '@featit/shared/addressing/legacyAddress';
import { appRouteOf, parseAddress, workAreaReferenceOf } from '@featit/shared/addressing/paths';
import { workAreaQualifiedLabel } from '@featit/shared/addressing/workAreaRouteRef';
import {
  chromeLabelText,
  recordLabel,
  viewLabel,
  type ChromeLabelType,
  type ChromeTitleKeyType,
  type ChromeTranslateFn,
} from '@featit/shared/chrome-label';
import { customerAgencyNameOf } from '@featit/shared/customer-sender';
import { isGuestWorkArea } from '@featit/shared/guest-work-area';
import { formatNumber, type AnyLocaleType } from '@featit/shared/i18n/core';
import { findWorkAreaByRouteRef, resolveRouteWorkArea } from '@/routing/workAreaRoute';

interface ProjectTitleSource {
  slug: string;
  name: string;
  namespace?: string | null;
  namespaceName?: string | null;
  namespaceIsOwn?: boolean;
  /** PX-33: a guest's work area is named to them by its agency, never by its own name. */
  access?: { guest?: boolean } | null;
}

/**
 * The installation itself, used where a route belongs to no work area at all.
 */
const PRODUCT_SCOPE = 'FEATit';

/** The view whose waiting count the tab carries, named once so nothing tests for the key twice. */
const DECISIONS_VIEW: ChromeTitleKeyType = 'chromeTitles.decisions';
/** I-0900: "Tasks for me" carries the same `(n)` prefix rule as Decisions. */
const TASKS_VIEW: ChromeTitleKeyType = 'chromeTitles.tasks';

/** What the reader is looking *at*, beyond what the address alone can say. */
interface IDocumentTitleScopeFields {
  /**
   * All work areas is the scope on screen (I-0405), whatever the address happens to be.
   *
   * ⛔ Needed because the scope outlives the address family. `/-/work-areas/*` is All work areas and
   * says so in the path, but a reader who chose All and then opened `/-/decisions` is still under All
   * — and a tab naming one work area over a list spanning every one of them is the exact failure
   * `isInstallationPath` exists to prevent, one scope over.
   */
  allWorkAreas?: boolean;
  /**
   * The scope the screen itself decided (`addressFrame`): an address that leads nowhere
   * and names no work area is the product's, whatever is selected (I-0776).
   */
  scopeName?: string;
  /**
   * T-0848 (under T-0838): the name of the view open at `views/<id>`, as the bar shows it
   * (`viewNameOfUrlId`): a built-in's already in the reader's language, a saved view's as typed.
   * Unknown while the saved views load, and the tab says the section's name meanwhile.
   */
  openedViewName?: string;
  /**
   * F-0621: draws the view half in the reader's language (`shellWords.pageName`). The view is named
   * in English, its identity (the waiting count is matched on it); a page's own title is not passed
   * through it, and the scope half, a work area's own name, never is.
   */
  viewName?: (english: string) => string;
  /** F-0621: every work area at once, as a scope, in the reader's language. Absent: "Work areas". */
  allWorkAreasName?: string;
  /**
   * F-0774 ONE-8: on Settings with a section of the work-area group open, the chosen work area's
   * qualified name: the tab then reads as the heading does, "Work area settings" in that work area.
   */
  settingsWorkArea?: string;
  /**
   * U-0060: the name of the document open at `documents/<id>`, as the page announces it
   * (`announcedRecordTitle`). Unknown while it loads, and the tab says "Documents" meanwhile.
   */
  documentName?: string;
  /**
   * U-0060 (TT-1): the key and title of the item open at `items/<key>`, from the item store. Unknown
   * while it loads, and the tab says the item's key meanwhile.
   */
  itemName?: string;
}

/**
 * The waiting counts the tab carries, with the reader's locale they are written in ("(1,234)",
 * „(1.234)“, F-0621). A scope that carries a count names its locale; there is no default.
 */
type WaitingCountsType =
  | {
      waitingDecisions?: undefined;
      waitingTasks?: undefined;
      urgentTasksLabel?: undefined;
      locale?: AnyLocaleType;
    }
  | {
      /**
       * Decisions waiting for this reader in the scope on screen — **the sidebar badge's own
       * number**, computed by `decisionsWaitingInScope` and passed in rather than worked out again
       * here (I-0611).
       */
      waitingDecisions?: number;
      /** I-0900: tasks waiting for this reader in the scope on screen — the same rule, on `TASKS_VIEW`. */
      waitingTasks?: number;
      /**
       * I-2602 (TF-38): the tasks count's urgent part, already in the reader's words ("2 urgent"),
       * or absent when nothing waiting is urgent.
       */
      urgentTasksLabel?: string;
      locale: AnyLocaleType;
    };

export type DocumentTitleScopeType = IDocumentTitleScopeFields & WaitingCountsType;

/**
 * The tab title, which always reads `<scope> - <view>`, and `(<n>) <scope> - <view>` where something
 * is waiting on the view it is waiting on.
 *
 * The scope is the work area being looked at, because that is the thing a person has several of
 * and needs to tell apart across windows. The view names the page within it. Two halves, always
 * both present: a title that says only "Views" does not say which work area's.
 *
 * T-0838 (IN-26): in the reader's language, through `t`, the page's own: the view's name and the
 * scope's words are the chrome's (`chromeTitles`); a work area's or a page's own name is as given.
 */
export function documentTitleForPath(
  t: ChromeTranslateFn,
  pathname: string,
  projects: ProjectTitleSource[],
  /**
   * The selected work area's name — `ProjectStore.selectedWorkAreaName`, **not**
   * `selectedProject?.name`. The second is `undefined` until the work areas have been fetched, and
   * a slug-less work-area route handed `undefined` fell through to the product name and told a
   * reader in Evorix they were looking at the installation (I-0611).
   */
  selectedProjectName?: string,
  dashboardPageTitle?: string,
  scope: DocumentTitleScopeType = {},
): string {
  const settingsWorkArea =
    appRouteOf(pathname) === '/settings' ? scope.settingsWorkArea : undefined;
  const view = settingsWorkArea
    ? viewLabel('workAreaSettings')
    : dashboardPageTitle
      ? recordLabel(dashboardPageTitle)
      : (openedViewLabel(pathname, scope.openedViewName) ??
        openedDocumentLabel(pathname, scope.documentName) ??
        openedItemLabel(pathname, scope.itemName) ??
        viewNameForPath(pathname));
  const where = settingsWorkArea ?? scopeForPath(t, pathname, projects, selectedProjectName, scope);
  const prefix = scope.locale
    ? waitingPrefix(
        view,
        scope.waitingDecisions,
        scope.waitingTasks,
        scope.locale,
        scope.urgentTasksLabel,
      )
    : '';
  return `${prefix}${where} - ${chromeLabelText(view, t)}`;
}

/**
 * `(3) ` at the **front** of the title, on the view the number is about.
 *
 * ⭐ In front because a browser truncates the **end** of a tab title, and an inactive tab is the
 * narrow one — which is precisely the tab Jay asked for this for: *"so it can be easily spotted also
 * on non active tabs"*. Appended, the number is the first thing the truncation eats, on exactly the
 * tabs it was added for.
 *
 * ⛔ Nothing at all when nothing is waiting. `(0)` is a number to read and then dismiss on every tab
 * of every work area with an empty queue, which is most of them; and `(...)` while the first read is
 * in flight would put a flicker at the front of every load.
 */
function waitingPrefix(
  view: ChromeLabelType,
  waitingDecisions: number | undefined,
  waitingTasks: number | undefined,
  locale: AnyLocaleType,
  urgentTasksLabel?: string,
): string {
  const waiting =
    view.key === DECISIONS_VIEW
      ? waitingDecisions
      : view.key === TASKS_VIEW
        ? waitingTasks
        : undefined;
  if (!waiting || waiting < 0) return '';
  // TF-38: on Tasks for me, the urgent part follows the count: "(26 · 2 urgent)".
  const urgent = view.key === TASKS_VIEW && urgentTasksLabel ? ` · ${urgentTasksLabel}` : '';
  return `(${formatNumber(locale, waiting)}${urgent}) `;
}

/**
 * PX-33 (I-2384, point 3): the agency a customer reads for a work area, by the organisation it lies
 * in; null for a person's own namespace or none named. Never the work area's own name.
 */
function customerAgencyOf(project: ProjectTitleSource): string | null {
  return customerAgencyNameOf({
    organisation: project.namespaceIsOwn ? null : project.namespaceName,
  });
}

/**
 * The selected work area's name as the tab names it on a route with no work area in it: a customer's
 * (PX-33) is the agency, or the product; a member's is `name` (`ProjectStore.selectedWorkAreaName`).
 */
export function tabNameOfSelected(
  selected: ProjectTitleSource | undefined,
  name: string | undefined,
): string | undefined {
  if (selected && isGuestWorkArea(selected)) return customerAgencyOf(selected) ?? PRODUCT_SCOPE;
  return name;
}

/** What the frame calls a work area: a member reads "Namespace / Work area", a customer (PX-33) the agency. */
function frameScopeOf(t: ChromeTranslateFn, project: ProjectTitleSource): string {
  if (!isGuestWorkArea(project)) return workAreaQualifiedLabel(project);
  return customerAgencyOf(project) ?? t('chromeTitles.guestPages');
}

function scopeForPath(
  t: ChromeTranslateFn,
  pathname: string,
  projects: ProjectTitleSource[],
  selectedProjectName: string | undefined,
  scope: DocumentTitleScopeType,
): string {
  if (scope.scopeName) return scope.scopeName;
  const appRoute = appRouteOf(pathname);
  // `/-/my-ideas` (F-0461) lists the reader's ideas in every work area they read, so it is no one
  // work area's page and must not borrow the selected one's name for the tab.
  if (
    appRoute === '/work-areas' ||
    appRoute?.startsWith('/work-areas/') ||
    appRoute === '/my-ideas'
  ) {
    // Every work area at once, which is a scope of its own rather than a missing one.
    return t('chromeTitles.workAreas');
  }
  if (isInstallationPath(pathname)) return PRODUCT_SCOPE;
  // AC-AD12: a namespace's own address names the namespace.
  const address = parseAddress(pathname);
  if (address.kind === 'namespace') return address.namespace;

  const routeProjectSlug = workAreaReferenceOf(pathname);
  if (routeProjectSlug) {
    // The slug until the names arrive. It is the worse of the two labels and the right one to show:
    // it is what the address bar already says, and it never claims the wrong work area.
    // I-0655, AC-M14: `Acme / Tracker`, so two work areas called Tracker never share a tab title.
    const project = findWorkAreaByRouteRef(projects, routeProjectSlug);
    // PX-33: a customer's tab names the agency, or the product: never the work area's own name.
    if (project && isGuestWorkArea(project)) return customerAgencyOf(project) ?? PRODUCT_SCOPE;
    return project ? workAreaQualifiedLabel(project) : routeProjectSlug;
  }
  // A route with no work area in it takes the scope the reader chose, and All is one of the answers.
  if (scope.allWorkAreas) return t('chromeTitles.workAreas');
  return selectedProjectName ?? PRODUCT_SCOPE;
}

/**
 * Whether a route belongs to the installation rather than to any one work area.
 *
 * Stated as the exceptions, not as the rule. The rule used to be the other way round — a list of
 * the paths that *were* work-area routes, everything else falling back to the product name — and a
 * list like that only ever loses entries. It named `/views` but not `/views/:viewId`, so opening a
 * saved view dropped the work area out of the title while the catalog it was opened from kept it.
 *
 * Inverted, a route added tomorrow is scoped by default and a title can only lose its work area by
 * someone deciding here that it has none.
 */
function isInstallationPath(pathname: string): boolean {
  const route = appRouteOf(pathname);
  return (
    route === '/settings' ||
    // F-0264: GitLab's return address after a person links their account names no work area.
    route === '/settings/streams/account-callback' ||
    route === '/admin' ||
    route === '/profile' ||
    // T-0320: the app's More tab is the navigation itself, whichever work area is selected.
    route === '/more' ||
    // T-0318: the iPhone app's warm spare, drawing nothing until it is routed to a screen.
    route === '/shell-spare' ||
    // F-0601 AMX-6a to AMX-6f: `/infrastructure` and a machine's Permissions only redirect to the
    // Agents explorer, which is titled as the Agents page is.
    // F-0630: the inbox lists every session on every machine, whichever work area is selected.
    route === '/conversations' ||
    Boolean(route?.startsWith('/conversations/')) ||
    // I-0334: an organisation's People page and an invitation link name no work area of their own.
    Boolean(route?.startsWith('/organisations/')) ||
    route === '/invite' ||
    // F-0271: what is shared with a guest spans every work area they are a guest of.
    route === '/shared' ||
    // I-2447: one shared request on its own spans no work area of the reader's either.
    Boolean(route?.startsWith('/shared/')) ||
    // F-0617: an artifact shared with this verified person is theirs, not any work area's.
    Boolean(route?.startsWith('/artifact-shares/'))
    // ⛔ `/agents` used to be listed here, on the reasoning that "the fleet belongs to the person:
    // the same agents whichever work area is selected". That stopped being true on 2026-09-11, when
    // the view began narrowing to the selected work area (AC-11.14) — it shows that work area's
    // sessions and says how many it held back. A tab reading "FEATit - Agents" over a list scoped to
    // Evorix names the wrong scope, which is the one thing this function exists to prevent.
    // With no work area selected the fleet IS every work area, and `scopeForPath` falls back to the
    // product name on its own.
  );
}

/**
 * The segments that name the view: the route after a work area's `/-/`, after `/-/work-areas`,
 * after `/-/namespaces/:id`, or after `/-/` — the same positions `barTitleOf` reads (`@featit/shared`).
 *
 * ⛔ A namespace's pages (F-0383) were read from `namespaces`, so `namespaces` was taken for the view
 * and every one of them — Home, Activity, Decisions, Tasks, Views — fell through to "Work items"
 * (I-1178).
 *
 * ⛔ Titles used to test `pathname.includes('/documents')`, and a work-area slug is part of the
 * path, so a work area slugged `documents-archive` had every page called "Documents" (I-0385). A
 * view is named by the segment in the view's position and nothing else — a slug, a namespace or a
 * subgroup can never be read as one, whatever it is called.
 */
export function viewSegments(pathname: string): string[] {
  const address = parseAddress(pathname);
  if (address.kind !== 'app' && address.kind !== 'work-area') return [];
  const segments = address.route.split('/').filter(Boolean);
  if (address.kind === 'app' && segments[0] === 'work-areas') return segments.slice(1);
  if (address.kind === 'app' && segments[0] === 'namespaces' && segments.length >= 2) {
    return segments.slice(2);
  }
  return segments;
}

/**
 * A view opened from Views is named by its own name, as the bar names it; the Views overview itself
 * keeps the section's. The name arrives said (a built-in through `viewNameIn`), so it is drawn as it is.
 */
function openedViewLabel(pathname: string, name: string | undefined): ChromeLabelType | null {
  const [section, viewId] = viewSegments(pathname);
  return section === 'views' && viewId && name ? recordLabel(name) : null;
}

/**
 * U-0060: a document opened from Documents is named by its own title, as its heading is; the list
 * keeps the section's name.
 */
function openedDocumentLabel(pathname: string, name: string | undefined): ChromeLabelType | null {
  const [section, documentId, requestKey] = viewSegments(pathname);
  if (!name) return null;
  if (section === 'documents' && documentId) return recordLabel(name);
  // F-0710 CRQ-12 (I-2492): a request a guest opened on its own, by the title its page announces.
  return section === 'shared' && documentId && requestKey ? recordLabel(name) : null;
}

/**
 * U-0060 (TT-1): the key of the item open at `items/<key>`, in the list's panel or on its own page;
 * `null` on the list, the new-item form and every other route.
 */
export function openedItemKeyOf(pathname: string): string | null {
  const address = parseAddress(pathname);
  if (address.kind !== 'work-area') return null;
  const [section, key, rest] = viewSegments(pathname);
  if (section !== 'items' || !key || key === 'new' || rest) return null;
  return decodeURIComponent(key);
}

/** U-0060 (TT-1): an open item is named by its key and title, as its page heading is; by its key until then. */
function openedItemLabel(pathname: string, name: string | undefined): ChromeLabelType | null {
  const key = openedItemKeyOf(pathname);
  return key ? recordLabel(name || key) : null;
}

/** The view's name, by its `chromeTitles` key (T-0838); the tab's text is `t`'s. */
function viewNameForPath(pathname: string): ChromeLabelType {
  const address = parseAddress(pathname);
  const route = address.kind === 'app' || address.kind === 'work-area' ? address.route : null;
  if (address.kind === 'namespace') return viewLabel('workAreas');
  if (address.kind === 'work-area' && route === '') return viewLabel('home');
  if (address.kind === 'app' && (route === '/home' || route === '/work-areas'))
    return viewLabel('home');
  // I-1178: a namespace's bare address is its Home (F-0383), the same page `/-/work-areas` is.
  if (address.kind === 'app' && /^\/namespaces\/[^/]+\/?$/.test(route ?? ''))
    return viewLabel('home');
  // U-0060: a page opened for one item (`pages/<id>/items/<key>`) is that page, as the bar names it.
  if (address.kind === 'work-area' && /^\/pages\/[^/]+(\/items\/[^/]+)?\/?$/.test(route ?? ''))
    return viewLabel('page');
  // U-0060: a repository file is called by its own name, as its heading is; the scope half already
  // names the organisation and work area. It read "Work items", the fallback, before.
  const fileName = address.kind === 'work-area' ? repositoryFileNameOf(route ?? '') : null;
  if (fileName) return recordLabel(fileName);
  // U-0060: an address that names no file (`files`, `folders/<folder>/files`) is still the file page,
  // which says so ("This address names no file"); it read "Work items", the fallback, on dev.
  if (address.kind === 'work-area' && /^\/(?:folders\/[^/]+\/)?files\/?$/.test(route ?? ''))
    return viewLabel('file');
  const appRoute = address.kind === 'app' ? route : null;
  const [view, detail, organisationSection] = viewSegments(pathname);
  if (view === 'activity') return viewLabel('activity');
  // ⛔ By segment, like every other view: the decisions branch wrote these as `includes('/decisions')`
  // before I-0385 landed, which would have called every page of a work area slugged `decisions-*`
  // "Decisions" again.
  if (view === 'decisions')
    return detail === 'new' ? viewLabel('newDecision') : viewLabel('decisions');
  // I-0900: same segment-based rule as Decisions, directly below it in the sidebar.
  if (view === 'tasks') return viewLabel('tasks');
  if (view === 'views') return viewLabel('views');
  if (view === 'documents') return viewLabel('documents');
  // U-0060: a commit's changed files (F-0564), as the bar names it.
  if (view === 'commits' && detail) return viewLabel('commit');
  if (view === 'roadmap') return viewLabel('roadmap');
  // F-0321: a work area's Parts tree, and one part's page.
  if (view === 'parts') return detail ? viewLabel('part') : viewLabel('parts');
  // F-0424: a work area's Schedules, one schedule, and the form that defines one.
  // I-1896: a run's address (`schedules/<slug>/runs/<id>`) names the run, not its schedule.
  if (view === 'schedules' && organisationSection === 'runs') return viewLabel('run');
  // I-1955: a schedule's form, as the phone bar names it (`barTitleOf`): one page, one name.
  if (view === 'schedules' && detail && detail !== 'new' && organisationSection === 'edit')
    return viewLabel('editSchedule');
  if (view === 'schedules')
    return detail === 'new'
      ? viewLabel('newSchedule')
      : detail
        ? viewLabel('schedule')
        : viewLabel('schedules');
  if (appRoute === '/settings' || appRoute === '/admin') return viewLabel('globalSettings');
  if (view === 'settings') return viewLabel('settings');
  if (view === 'admin') return viewLabel('admin');
  if (view === 'profile') return viewLabel('profile');
  if (appRoute === '/more') return viewLabel('more');
  if (appRoute === '/shell-spare') return viewLabel('loading');
  if (appRoute === '/agents' || appRoute === '/agents/preview/usage') return viewLabel('agents');
  // F-0630: the inbox, one conversation in it, and their seeded twin.
  if (appRoute === '/conversations' || appRoute?.startsWith('/conversations/'))
    return viewLabel('conversations');
  /*
   * F-0347 (I-1023): an organisation's own Settings — `/organisations/:id/settings`, every section
   * chosen by `?section=` rather than by the path — is its own view, checked ahead of the prefix
   * below. Unchecked, the prefix match caught it first and called every section "People", the
   * legacy `/organisations/:id` address's own, correct name (OSE-3) and still returned two lines down.
   */
  if (appRoute?.startsWith('/organisations/') && organisationSection === 'settings') {
    return viewLabel('settings');
  }
  if (appRoute?.startsWith('/organisations/')) return viewLabel('people');
  if (appRoute === '/invite') return viewLabel('invitation');
  if (appRoute === '/shared' || appRoute?.startsWith('/shared/')) {
    return viewLabel('sharedWithYou');
  }
  // F-0617: one artifact shared with this person, read as a page of what is shared with them.
  if (appRoute?.startsWith('/artifact-shares/')) return viewLabel('sharedWithYou');
  if (appRoute?.startsWith('/feedback/')) return viewLabel('feedback');
  // F-0461 (OF-31): the ideas the reader reported, in every work area.
  if (appRoute === '/my-ideas') return viewLabel('myIdeas');
  if (appRoute?.startsWith('/work-areas/choose/')) return viewLabel('chooseWorkArea');
  // F-0530 (AC-19.4): the work-area picker's edit mode, named as its own heading names it.
  if (appRoute === '/work-areas/edit') return viewLabel('editWorkAreas');
  if (view === 'items' && detail === 'new') return viewLabel('newItem');
  // ⛔ The fallback, and the work items list's own name. A route that lands here without being the
  // list is a route nobody named; `documentTitleRoutes.test.ts` holds every route to the view the
  // bar decided for it, so one cannot land here silently again (I-1178).
  return viewLabel('workItems');
}

/**
 * The small line above the view title in the phone header: which work area this page is about
 * (I-0655, AC-M24).
 *
 * ⛔ **From the route first, never from the selection alone.** On `/acme/tracker/…` the address names the
 * work area, and the selection can still be the one the reader came from. That is what the
 * ambiguous-address chooser showed before: the previous work area's name above a page asking
 * *which* work area was meant.
 */
export function phoneHeaderScope(
  t: ChromeTranslateFn,
  pathname: string,
  projects: ProjectTitleSource[],
  selected: ProjectTitleSource | undefined,
  allWorkAreas: boolean,
  /** F-0774 ONE-8: on Settings, the work area whose section is open, as the tab names it. */
  settingsWorkArea?: string,
): string {
  if (settingsWorkArea && appRouteOf(pathname) === '/settings') return settingsWorkArea;
  // An address from before R-5 may still name a bare slug while it is being replaced (AC-AD5).
  const legacy = legacyAddressTarget(pathname);
  const routeRef =
    workAreaReferenceOf(pathname) ?? (legacy?.kind === 'bare-work-area' ? legacy.slug : null);
  if (routeRef) {
    if (projects.length === 0) return routeRef;
    const resolution = resolveRouteWorkArea(projects, routeRef);
    if (resolution.outcome === 'ambiguous') return t('chromeTitles.chooseWorkArea');
    const project = findWorkAreaByRouteRef(projects, routeRef);
    return project ? frameScopeOf(t, project) : routeRef;
  }
  if (allWorkAreas) return t('chromeTitles.allWorkAreas');
  return selected ? frameScopeOf(t, selected) : 'FEATit';
}

/**
 * U-0060: the file a work area's route names (`files/<path>` or `folders/<folder>/files/<path>`), by
 * its last segment, decoded as the file page decodes it; `null` for any other route.
 */
function repositoryFileNameOf(route: string): string | null {
  const match = /^\/(?:folders\/[^/]+\/)?files\/(.+?)\/?$/.exec(route);
  const last = match?.[1].split('/').pop();
  if (!last) return null;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}
